// What? A React component for displaying detailed company information
// Why? To show comprehensive company profile data
// How? Fetches and displays detailed company information including pipeline, people, and news

import React, { Suspense, lazy, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useBKGRead, useBKGCompanyInvestors } from "../hooks";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  Typography,
  Button,
  Box,
  Stack,
  Alert,
  Card,
  CardContent,
  Grid,
  Link as JoyLink,
  Chip,
} from "@mui/joy";
import Loading from "../../../components/Loading";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactsIcon from "@mui/icons-material/Contacts";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageHeader from "../../../components/PageHeader";
import Page from "../../../components/Pages/Page";
import AutodbTooltipWrapper, {
  AutodbTooltipHost,
  getLinkedinConfidenceLevel,
} from "../components/AutodbTooltipWrapper";
import LinkedInConfidenceBadge from "../components/LinkedInConfidenceBadge";
import { CompanyOperatingStatus } from "../components/CompanyOperatingStatus";
import { FloatingChatWidget } from "../components/BKGAgent";
import CompanyCatalystEvents from "../components/CompanyCatalystEvents";
import { stripSourceRefs } from "../../../utils/stripSourceRefs";
import {
  autodbScalar,
  formatHeadquartersAddress,
  formatLatestFunding,
  getArrayFieldItems,
  getDevelopmentStage,
  getScalarFieldValue,
  getStringFieldValue,
  getTherapeuticAreas,
} from "../utils/autodbValues";
import {
  buildVCHref,
  COMPANY_PROFILE_BACK_SOURCE,
  VC_PROFILE_BACK_SOURCE,
  useProfileBackTag,
  type CompanyProfileBackSource,
} from "../utils/profileBackNavigation";
import type { VCInvestorReference } from "../types";
import {
  useTutorialTarget,
  useTutorialHint,
} from "../../../components/Tutorial";

const PDVCompanyConnections = lazy(
  () => import("../components/PDVCompanyConnections"),
);

/**
 * Extract VC investor references from a VC index response.
 * Parses document IDs (format: "workspace/venture_capital/vc_id") to build
 * navigable references back to VC profile pages.
 */
function parseInvestorReferences(
  items: Array<{ _id?: string; value?: Record<string, any>; name?: string }>,
): VCInvestorReference[] {
  return items
    .map((item) => {
      const vcId = item?._id;
      if (!vcId) return null;

      const vcName =
        item?.value?.name?.value ?? item?.value?.name ?? item?.name;

      const refParts = vcId.split("/");
      const vcIndex = refParts.indexOf("venture_capital");
      const extractedId =
        vcIndex >= 0
          ? refParts.slice(vcIndex + 1).join("/")
          : refParts[refParts.length - 1];

      const displayName =
        typeof vcName === "string" && vcName.trim()
          ? vcName
          : extractedId
              .replace(/_/g, " ")
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");

      return { id: extractedId, name: displayName, documentId: vcId };
    })
    .filter((ref): ref is VCInvestorReference => ref !== null);
}

const BSON_OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;

const getArrayItems = (field: unknown): any[] => getArrayFieldItems(field);

const resolveDocumentId = (
  workspaceId: string,
  routeId?: string,
): string | undefined => {
  if (!routeId) return undefined;
  if (routeId.includes("/")) return routeId;
  if (BSON_OBJECT_ID_PATTERN.test(routeId)) return routeId;
  return `${workspaceId}/company/${routeId}`;
};

const CompanyProfile: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const params = useParams<{ company_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const company_id = params.company_id;
  const navigationState = location.state as {
    companyData?: any;
    documentId?: string;
  } | null;

  // See `useProfileBackTag` / `COMPANY_PROFILE_BACK_SOURCE` for the contract.
  const backTag = useProfileBackTag<CompanyProfileBackSource>();
  // Require fromName for cross-profile, so label and destination always agree.
  // Without a name, fall through to the generic listing label/destination so
  // we never render "Back to VC Firm" pointing somewhere the user can't verify.
  const backsToVCProfile =
    backTag.from === COMPANY_PROFILE_BACK_SOURCE.vcProfile &&
    !!backTag.fromId &&
    !!backTag.fromName;
  const backsToCatalyst =
    backTag.from === COMPANY_PROFILE_BACK_SOURCE.catalystEvents;

  const backLabel = backsToVCProfile
    ? `Back to ${backTag.fromName}`
    : backsToCatalyst
      ? "Back to Catalyst Events"
      : "Back to Companies";

  const handleBackClick = () => {
    if (!workspace?.id) return;
    // In-app history exists: pop it. The previous URL carries its own ?from=
    // tag (or no tag if it's a listing), so the label stays honest at each
    // step as the user backs up through the chain.
    if (!backTag.inFreshTabContext) {
      navigate(-1);
      return;
    }
    // Fresh tab / deep link / continuing a fresh-tab back chain: rebuild the
    // destination from the tag. Use replace so we don't keep the previous
    // entry around (navigate(-1) from the destination would pop straight
    // back here — a loop). Carry freshTabBack forward so the destination
    // stays in this regime.
    if (backsToVCProfile) {
      navigate(
        `/workspace/${workspace.id}/bkg/venture-capital/${backTag.fromId}`,
        { replace: true, state: { freshTabBack: true } },
      );
    } else if (backsToCatalyst) {
      navigate(`/workspace/${workspace.id}/bkg/catalyst-events`, {
        replace: true,
      });
    } else {
      navigate(`/workspace/${workspace.id}/bkg/companies`, { replace: true });
    }
  };

  const BKGReadAPI = useBKGRead();
  const fetchCompanyInvestors = useBKGCompanyInvestors();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [company, setCompany] = useState<any>();
  const [vcInvestors, setVcInvestors] = useState<VCInvestorReference[]>([]);
  const pdvEnabled = workspace?.enabled_integrations?.includes("pdv") === true;

  // One-shot hint teaching cross-entity navigation — shown only when this
  // company actually has indexed investors to click through to.
  const investorsTutorialRef = useTutorialTarget("bkg-company-investors");
  useTutorialHint(
    {
      id: "bkg-company-profile-intro",
      targetId: "bkg-company-investors",
      title: "Explore the full profile",
      body: "Dive into pipeline assets, leadership and investors. Click an investor to jump straight to their VC firm profile.",
      placement: "top",
    },
    !loading && vcInvestors.length > 0,
  );

  useEffect(() => {
    const fetchProfile = async () => {
      if (!workspace || !company_id) return;

      const resolvedDocumentId =
        navigationState?.documentId ??
        resolveDocumentId(workspace.id, company_id);

      if (!resolvedDocumentId) {
        setError("Missing company identifier.");
        setLoading(false);
        return;
      }

      try {
        const data = await BKGReadAPI(
          workspace.id,
          "company",
          resolvedDocumentId,
        );

        // Fetch assets
        const fetchAssetData = async (asset: any) => {
          if (asset?.reference) {
            const asset_data = await BKGReadAPI(
              workspace.id,
              "company_pipeline_asset",
              asset.reference,
            );
            asset.value = asset_data?.value;
          }
        };

        const fetchPersonData = async (person: any) => {
          if (person?.reference) {
            const person_data = await BKGReadAPI(
              workspace.id,
              "company_person",
              person.reference,
            );
            person.value = person_data?.value;
          }
        };

        const promises = [];
        for (const asset of getArrayItems(
          data?.value?.pipeline?.value?.assets ?? data?.value?.pipeline?.assets,
        )) {
          promises.push(fetchAssetData(asset));
        }
        for (const person of getArrayItems(
          data?.value?.leadership?.value?.people ??
            data?.value?.leadership?.people,
        )) {
          promises.push(fetchPersonData(person));
        }
        for (const contact of getArrayItems(data?.value?.hubspot_contacts)) {
          promises.push(fetchPersonData(contact));
        }
        await Promise.all(promises);

        setCompany(data);
        setError(null);

        // Fetch VC investors that have this company in their portfolio (reverse lookup)
        if (resolvedDocumentId) {
          try {
            const investorResponse = await fetchCompanyInvestors(
              workspace.id,
              resolvedDocumentId,
            );
            setVcInvestors(
              parseInvestorReferences(investorResponse?.items ?? []),
            );
          } catch (investorError) {
            console.error(
              "Failed to fetch VC investors for company:",
              investorError,
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch company profile:", error);
        setError("Failed to load company profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- workspace?.id is sufficient, BKGReadAPI is stable
  }, [workspace?.id, company_id, navigationState?.documentId]);

  // Render
  const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const renderLatestFunding = (latest_funding: any) => {
    return formatLatestFunding(latest_funding);
  };

  const renderLocation = (company: any) => {
    return formatHeadquartersAddress(company);
  };

  const renderAsset = (asset: any) => {
    const assetValue = asset?.value ?? asset;
    const assetName = getScalarFieldValue(assetValue?.name);
    const indication = getScalarFieldValue(assetValue?.indication);
    const developmentStage = getDevelopmentStage(asset);
    const tas = getTherapeuticAreas(asset);
    const tes = getArrayItems(assetValue?.technology)
      .map((item: any) => autodbScalar(item))
      .filter(
        (item): item is string => typeof item === "string" && item.length > 0,
      );

    return (
      assetName && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="h4" sx={{ mb: 1 }}>
              {assetName}
            </Typography>
            {developmentStage && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  level="body-xs"
                  color="primary"
                  sx={{
                    bgcolor: "primary.50",
                    px: 1,
                    py: 0.5,
                    borderRadius: "sm",
                    display: "inline-block",
                  }}
                >
                  {developmentStage}
                </Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              {indication && (
                <Grid xs={12} md={6}>
                  <Box>
                    <Typography
                      level="body-xs"
                      color="neutral"
                      sx={{ fontWeight: 500 }}
                    >
                      Indication
                    </Typography>
                    <AutodbTooltipWrapper info={assetValue?.indication}>
                      <Typography level="body-sm">{indication}</Typography>
                    </AutodbTooltipWrapper>
                  </Box>
                </Grid>
              )}
              {developmentStage && (
                <Grid xs={12} md={6}>
                  <Box>
                    <Typography
                      level="body-xs"
                      color="neutral"
                      sx={{ fontWeight: 500 }}
                    >
                      Development Stage
                    </Typography>
                    <AutodbTooltipWrapper
                      info={assetValue?.development_stage ?? assetValue?.stage}
                    >
                      <Typography level="body-sm">
                        {developmentStage}
                      </Typography>
                    </AutodbTooltipWrapper>
                  </Box>
                </Grid>
              )}
              {tas.length > 0 && (
                <Grid xs={12} md={6}>
                  <Box>
                    <Typography
                      level="body-xs"
                      color="neutral"
                      sx={{ fontWeight: 500 }}
                    >
                      Therapeutic Area
                    </Typography>
                    <AutodbTooltipWrapper info={assetValue?.therapeutic_area}>
                      <Typography level="body-sm">{tas.join(", ")}</Typography>
                    </AutodbTooltipWrapper>
                  </Box>
                </Grid>
              )}
              {tes.length > 0 && (
                <Grid xs={12} md={6}>
                  <Box>
                    <Typography
                      level="body-xs"
                      color="neutral"
                      sx={{ fontWeight: 500 }}
                    >
                      Technology
                    </Typography>
                    <AutodbTooltipWrapper info={assetValue?.technology}>
                      <Typography level="body-sm">{tes.join(", ")}</Typography>
                    </AutodbTooltipWrapper>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )
    );
  };

  const renderPerson = (person: any) => {
    const personValue = person?.value ?? person;
    const personName = getScalarFieldValue(personValue?.name);
    const personRole =
      getScalarFieldValue(personValue?.role) ??
      getScalarFieldValue(personValue?.title);
    const personEmail = getStringFieldValue(personValue?.email);
    const personLinkedIn = getStringFieldValue(personValue?.linkedin);

    return (
      personName && (
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "sm",
                  bgcolor: "neutral.100",
                }}
              >
                <AccountCircleIcon
                  sx={{ fontSize: 40, color: "neutral.500" }}
                />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography level="title-md" sx={{ mb: 0.5 }}>
                  {personName}
                </Typography>
                {personRole && (
                  <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    {personRole}
                  </Typography>
                )}
                <Stack spacing={0.5}>
                  <AutodbTooltipWrapper info={personValue?.email}>
                    {personEmail && (
                      <Button
                        component="a"
                        href={`mailto:${personEmail}`}
                        variant="plain"
                        size="sm"
                        startDecorator={<EmailIcon />}
                        sx={{ justifyContent: "flex-start", p: 0 }}
                      >
                        {personEmail}
                      </Button>
                    )}
                  </AutodbTooltipWrapper>
                  <AutodbTooltipWrapper info={personValue?.linkedin} block>
                    {personLinkedIn && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Button
                          component="a"
                          href={personLinkedIn}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="plain"
                          size="sm"
                          startDecorator={<LinkedInIcon />}
                          sx={{ justifyContent: "flex-start", p: 0 }}
                        >
                          LinkedIn
                        </Button>
                        <LinkedInConfidenceBadge
                          level={getLinkedinConfidenceLevel(
                            personValue?.linkedin,
                          )}
                        />
                      </Stack>
                    )}
                  </AutodbTooltipWrapper>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )
    );
  };

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  return (
    <Page>
      {/* One tooltip serves every wrapped cell on this page. */}
      <AutodbTooltipHost />

      <PageHeader>
        <Typography level="h1">Company Profile</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            startDecorator={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleBackClick}
          >
            {backLabel}
          </Button>
          <Button
            startDecorator={<FileDownloadIcon />}
            variant="outlined"
            onClick={() => window.print()}
          >
            Export
          </Button>
        </Stack>
      </PageHeader>

      <Stack spacing={3}>
        {/* Error Display */}
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading />
        ) : company ? (
          <>
            {/* Company Header */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="h2" sx={{ mb: 2, fontSize: "2rem" }}>
                  {company?.value.name?.value}
                </Typography>
                <CompanyOperatingStatus company={company} />
                {company?.value.summary?.value && (
                  <Typography level="body-lg" sx={{ mb: 3 }}>
                    {stripSourceRefs(company?.value.summary?.value)}
                  </Typography>
                )}

                {/* External Links */}
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <AutodbTooltipWrapper info={company?.value?.webpage}>
                    {company?.value?.webpage?.value && (
                      <Button
                        component="a"
                        href={company?.value?.webpage?.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="sm"
                        startDecorator={<LanguageIcon />}
                      >
                        Website
                      </Button>
                    )}
                  </AutodbTooltipWrapper>
                  <AutodbTooltipWrapper info={company?.value?.linkedin}>
                    {company?.value?.linkedin?.value && (
                      <Button
                        component="a"
                        href={company?.value?.linkedin?.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="sm"
                        startDecorator={<LinkedInIcon />}
                      >
                        LinkedIn
                      </Button>
                    )}
                  </AutodbTooltipWrapper>
                </Stack>

                <Grid container spacing={2}>
                  <Grid xs={12} md={3}>
                    <AutodbTooltipWrapper info={company?.value?.headquarters}>
                      <Box>
                        <Typography
                          level="body-xs"
                          color="neutral"
                          sx={{ fontWeight: 500 }}
                        >
                          Location
                        </Typography>
                        <Typography level="body-sm">
                          {renderLocation(company)}
                        </Typography>
                      </Box>
                    </AutodbTooltipWrapper>
                  </Grid>
                  {company?.value?.number_of_employees?.value && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        info={company?.value?.number_of_employees}
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 500 }}
                          >
                            Employees
                          </Typography>
                          <Typography level="body-sm">
                            {company?.value?.number_of_employees?.value}
                          </Typography>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                  {company?.value?.latest_funding?.value && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        info={company?.value?.latest_funding}
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 500 }}
                          >
                            Latest Funding
                          </Typography>
                          <Typography level="body-sm">
                            {renderLatestFunding(
                              company?.value?.latest_funding?.value,
                            )}
                          </Typography>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                  {company?.value?.company_entity_type?.value && (
                    <Grid xs={12} md={3}>
                      <AutodbTooltipWrapper
                        info={company?.value?.company_entity_type}
                      >
                        <Box>
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ fontWeight: 500 }}
                          >
                            Company Type
                          </Typography>
                          <Typography level="body-sm">
                            {capitalizeFirstLetter(
                              company?.value?.company_entity_type?.value,
                            )}
                          </Typography>
                        </Box>
                      </AutodbTooltipWrapper>
                    </Grid>
                  )}
                  {company?.value?.hubspot_owner?.value && (
                    <Grid xs={12} md={3}>
                      <Box>
                        <Typography
                          level="body-xs"
                          color="neutral"
                          sx={{ fontWeight: 500 }}
                        >
                          HubSpot Owner
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <BadgeIcon
                            sx={{ fontSize: 16, color: "neutral.500" }}
                          />
                          <Typography level="body-sm">
                            {company?.value?.hubspot_owner?.value}
                          </Typography>
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {pdvEnabled && company?._id && (
              <Suspense
                fallback={
                  <Card variant="outlined">
                    <CardContent>
                      <Loading />
                    </CardContent>
                  </Card>
                }
              >
                <PDVCompanyConnections
                  workspaceId={workspace.id}
                  companyRef={company._id}
                />
              </Suspense>
            )}

            {/* VC Investors Section - Bidirectional link from company to investors */}
            {vcInvestors.length > 0 && (
              <Card variant="outlined" ref={investorsTutorialRef}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccountBalanceIcon color="primary" />
                      <Typography level="h3">VC Investors</Typography>
                    </Stack>
                    <Typography level="body-sm" color="neutral">
                      {vcInvestors.length} indexed venture capital{" "}
                      {vcInvestors.length === 1 ? "firm has" : "firms have"}{" "}
                      invested in this company
                    </Typography>
                    <Grid container spacing={2}>
                      {vcInvestors.map((investor) => (
                        <Grid xs={12} sm={6} md={4} key={investor.id}>
                          <Card variant="soft" sx={{ height: "100%" }}>
                            <CardContent>
                              <Stack spacing={1}>
                                <JoyLink
                                  href={
                                    workspace?.id
                                      ? buildVCHref(workspace.id, investor.id, {
                                          from: VC_PROFILE_BACK_SOURCE.companyProfile,
                                          fromId: company_id,
                                          fromName:
                                            company?.value?.name?.value ??
                                            undefined,
                                        })
                                      : undefined
                                  }
                                  component="a"
                                  onClick={(e) => {
                                    if (
                                      e.ctrlKey ||
                                      e.metaKey ||
                                      e.shiftKey ||
                                      e.button === 1
                                    )
                                      return;
                                    e.preventDefault();
                                    if (workspace?.id) {
                                      navigate(
                                        buildVCHref(workspace.id, investor.id, {
                                          from: VC_PROFILE_BACK_SOURCE.companyProfile,
                                          fromId: company_id,
                                          fromName:
                                            company?.value?.name?.value ??
                                            undefined,
                                        }),
                                        {
                                          state: {
                                            documentId: investor.documentId,
                                          },
                                        },
                                      );
                                    }
                                  }}
                                  level="title-sm"
                                  sx={{
                                    fontWeight: 600,
                                    textAlign: "left",
                                  }}
                                >
                                  {investor.name}
                                </JoyLink>
                                <Chip
                                  size="sm"
                                  variant="outlined"
                                  color="primary"
                                >
                                  Investor
                                </Chip>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Key Stats Section */}
            {company?.value?.key_stats?.value && (
              <Card>
                <CardContent>
                  <Typography level="h3" sx={{ mb: 2 }}>
                    Key Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid xs={12} md={3}>
                      <Box>
                        <Typography
                          level="body-xs"
                          color="neutral"
                          sx={{ fontWeight: 500 }}
                        >
                          Market Cap
                        </Typography>
                        <Typography level="body-sm">
                          {getScalarFieldValue(
                            company?.value?.key_stats?.value?.market_cap,
                          ) ?? ""}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <Box>
                        <Typography
                          level="body-xs"
                          color="neutral"
                          sx={{ fontWeight: 500 }}
                        >
                          Revenue (TTM)
                        </Typography>
                        <Typography level="body-sm">
                          {getScalarFieldValue(
                            company?.value?.key_stats?.value?.revenue_ttm,
                          ) ?? ""}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <Box>
                        <Typography
                          level="body-xs"
                          color="neutral"
                          sx={{ fontWeight: 500 }}
                        >
                          Cash
                        </Typography>
                        <Typography level="body-sm">
                          {getScalarFieldValue(
                            company?.value?.key_stats?.value?.cash,
                          ) ?? ""}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <Box>
                        <Typography
                          level="body-xs"
                          color="neutral"
                          sx={{ fontWeight: 500 }}
                        >
                          Debt
                        </Typography>
                        <Typography level="body-sm">
                          {getScalarFieldValue(
                            company?.value?.key_stats?.value?.debt,
                          ) ?? ""}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Pipeline Section */}
            {getArrayItems(
              company?.value?.pipeline?.value?.assets ??
                company?.value?.pipeline?.assets,
            ).length > 0 && (
              <Card>
                <CardContent>
                  <Typography level="h3" sx={{ mb: 2 }}>
                    {company?.value?.pipeline?.value?.url?.value ? (
                      <Button
                        component="a"
                        href={company?.value?.pipeline?.value?.url?.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="plain"
                        sx={{ p: 0, fontSize: "inherit" }}
                      >
                        Pipeline Assets
                      </Button>
                    ) : (
                      "Pipeline Assets"
                    )}
                  </Typography>
                  <Stack spacing={2}>
                    {getArrayItems(
                      company?.value?.pipeline?.value?.assets ??
                        company?.value?.pipeline?.assets,
                    ).map((asset: any, index: number) => (
                      <React.Fragment key={index}>
                        {renderAsset(asset)}
                      </React.Fragment>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* People Section */}
            {getArrayItems(
              company?.value?.leadership?.value?.people ??
                company?.value?.leadership?.people,
            ).length > 0 && (
              <Card>
                <CardContent>
                  <Typography level="h3" sx={{ mb: 2 }}>
                    Key People
                  </Typography>
                  <Grid container spacing={3}>
                    {getArrayItems(
                      company?.value?.leadership?.value?.people ??
                        company?.value?.leadership?.people,
                    ).map((person: any, index: number) => (
                      <Grid xs={12} md={6} key={index}>
                        {renderPerson(person)}
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* HubSpot Contacts Section */}
            {getArrayItems(company?.value?.hubspot_contacts).some((c: any) =>
              getScalarFieldValue((c?.value ?? c)?.name),
            ) && (
              <Card>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <ContactsIcon color="primary" />
                    <Typography level="h3">HubSpot Contacts</Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    {getArrayItems(company?.value?.hubspot_contacts)
                      .filter((c: any) =>
                        getScalarFieldValue((c?.value ?? c)?.name),
                      )
                      .map((contact: any, index: number) => (
                        <Grid xs={12} md={6} key={index}>
                          {renderPerson(contact)}
                        </Grid>
                      ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* News Section */}
            {company?.value?.latest_news?.value && (
              <Card>
                <CardContent>
                  <Typography level="h3" sx={{ mb: 2 }}>
                    Latest News
                  </Typography>
                  <Box>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      Company Progress and Strategic Developments
                    </Typography>
                    <Stack spacing={1}>
                      {stripSourceRefs(company?.value?.latest_news?.value)
                        .split("\n\n")
                        .map((paragraph: any, index: number) => (
                          <Typography level="body-sm" key={index}>
                            {paragraph}
                          </Typography>
                        ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Catalyst Events Section (live, grouped by month/year) */}
            <CompanyCatalystEvents
              workspaceId={workspace?.id ?? ""}
              companyId={company?._id}
            />
          </>
        ) : (
          <Alert color="neutral" variant="soft">
            No company data available.
          </Alert>
        )}
      </Stack>
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />
    </Page>
  );
};

export default CompanyProfile;
